import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import "./EmpDocument.css";

const API_BASE = (process.env.REACT_APP_API_URL || "http://localhost:3000")
  .replace(/\/$/, "")
  .replace(/\/api$/, "");

function readToken() {
  return (
    localStorage.getItem("th_employer_token") ||
    localStorage.getItem("th_token") ||
    localStorage.getItem("token") ||
    ""
  );
}

// Regular JSON fetch (no file)
function authFetch(path, opts = {}) {
  const token = readToken();
  const apiPath = path.startsWith("/api/") ? path : `/api${path.startsWith("/") ? path : `/${path}`}`;
  return fetch(`${API_BASE}${apiPath}`, {
    credentials: "include",
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
  });
}

// Multipart fetch for file uploads — NO Content-Type header (browser sets it with boundary)
function authFileFetch(path, formData) {
  const token = readToken();
  return fetch(`${API_BASE}${path}`, {
    method: "POST",
    credentials: "include",
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: formData,
  });
}

const DOC_TYPES = [
  { value: "offer_letter", label: "Offer Letter" },
  { value: "contract",     label: "Contract" },
  { value: "id_proof",     label: "ID Proof" },
  { value: "payslip",      label: "Payslip" },
  { value: "appraisal",    label: "Appraisal" },
  { value: "nda",          label: "NDA" },
  { value: "policy",       label: "Policy Document" },
  { value: "invoice",      label: "Invoice" },
  { value: "pdf",          label: "PDF" },
  { value: "doc",          label: "DOC / DOCX" },
  { value: "image",        label: "Image" },
  { value: "other",        label: "Other" },
];

const MIME_ICONS = {
  "application/pdf":    "📄",
  "application/msword": "📝",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "📝",
  "application/vnd.ms-excel": "📊",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "📊",
  "image/jpeg": "🖼️",
  "image/png":  "🖼️",
  "image/webp": "🖼️",
  "text/plain": "📃",
  "text/csv":   "📊",
};

function getFileIcon(doc) {
  if (doc.mimeType && MIME_ICONS[doc.mimeType]) return MIME_ICONS[doc.mimeType];
  const t = String(doc.type || "").toLowerCase();
  if (t.includes("pdf"))    return "📄";
  if (t.includes("doc"))    return "📝";
  if (t.includes("image") || t.includes("img")) return "🖼️";
  if (t.includes("sheet") || t.includes("excel") || t.includes("csv")) return "📊";
  return "📁";
}

function formatSize(bytes) {
  if (!bytes || isNaN(bytes)) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ── Toast ─────────────────────────────────────────────────────────
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div className={`emptoast ${type === "error" ? "emperror" : ""}`}>
      {type === "error" ? "⚠️" : "✅"} {message}
    </div>
  );
};

const EMPTY_FORM = {
  name: "", type: "", employee: "",
  department: "", notes: "", confidential: false,
};

// ── Main Component ────────────────────────────────────────────────
const HrDocumentManager = () => {
  const [documents,        setDocuments]        = useState([]);
  const [search,           setSearch]           = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [typeFilter,       setTypeFilter]       = useState("");
  const [showUpload,       setShowUpload]       = useState(false);
  const [uploadData,       setUploadData]       = useState(EMPTY_FORM);
  const [selectedFile,     setSelectedFile]     = useState(null);
  const [dragOver,         setDragOver]         = useState(false);
  const [uploadProgress,   setUploadProgress]   = useState(0);
  const [loading,          setLoading]          = useState(true);
  const [saving,           setSaving]           = useState(false);
  const [toasts,           setToasts]           = useState([]);
  const fileInputRef = useRef(null);

  // ── Toast helpers ───────────────────────────────────────────────
  const addToast = useCallback((msg, type = "success") => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message: msg, type }]);
  }, []);

  const removeToast = useCallback(id => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // ── Fetch documents ─────────────────────────────────────────────
  const fetchDocs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedEmployee) params.set("employee", selectedEmployee);
      if (typeFilter)       params.set("type", typeFilter);
      if (search.trim())    params.set("search", search.trim());

      const res  = await authFetch(
        `/api/recruiter/documents${params.toString() ? `?${params}` : ""}`
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Could not load documents.");
      setDocuments(Array.isArray(data.data) ? data.data : []);
    } catch (err) {
      addToast(err.message || "Could not load documents.", "error");
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [addToast, search, selectedEmployee, typeFilter]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  // ── Derived data ────────────────────────────────────────────────
  const employees = useMemo(() =>
    [...new Set(documents.map(d => d.employee).filter(Boolean))].sort(),
    [documents]
  );

  const filteredDocs = useMemo(() => {
    const q = search.trim().toLowerCase();
    return documents.filter(doc => {
      if (selectedEmployee && doc.employee !== selectedEmployee) return false;
      if (typeFilter && doc.type !== typeFilter) return false;
      if (q && !doc.name.toLowerCase().includes(q) &&
               !(doc.employee || "").toLowerCase().includes(q) &&
               !(doc.department || "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [documents, search, selectedEmployee, typeFilter]);

  // ── File selection ──────────────────────────────────────────────
  const handleFileSelect = (file) => {
    if (!file) return;
    setSelectedFile(file);
    // Auto-fill name from filename (strip extension)
    if (!uploadData.name) {
      setUploadData(p => ({
        ...p,
        name: file.name.replace(/\.[^/.]+$/, ""),
      }));
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files[0]);
  };

  // ── Upload submit ───────────────────────────────────────────────
  const handleUpload = async (e) => {
    e.preventDefault();

    if (!selectedFile) {
      addToast("Please select a file to upload.", "error");
      return;
    }
    if (!uploadData.name.trim()) {
      addToast("Document name is required.", "error");
      return;
    }
    if (!uploadData.type) {
      addToast("Document type is required.", "error");
      return;
    }

    setSaving(true);
    setUploadProgress(10);

    try {
      const fd = new FormData();
      fd.append("file",         selectedFile);
      fd.append("name",         uploadData.name.trim());
      fd.append("type",         uploadData.type);
      fd.append("employee",     uploadData.employee);
      fd.append("department",   uploadData.department);
      fd.append("notes",        uploadData.notes);
      fd.append("confidential", uploadData.confidential ? "1" : "0");

      setUploadProgress(40);
      const res  = await authFileFetch("/api/recruiter/documents", fd);
      setUploadProgress(80);
      const data = await res.json();

      if (!res.ok) throw new Error(data.error || "Upload failed.");

      setUploadProgress(100);
      addToast(`"${data.data?.name || uploadData.name}" uploaded successfully.`);

      // Reset modal
      setShowUpload(false);
      setUploadData(EMPTY_FORM);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setUploadProgress(0);

      await fetchDocs();
    } catch (err) {
      addToast(err.message || "Upload failed. Please try again.", "error");
      setUploadProgress(0);
    } finally {
      setSaving(false);
    }
  };

  // ── Download ────────────────────────────────────────────────────
  const handleDownload = async (doc) => {
    if (!doc.fileUrl) {
      addToast("No file attached to this document.", "error");
      return;
    }
    try {
      const res = await fetch(
        `${API_BASE}/api/recruiter/documents/${doc.id}/download`,
        { headers: { Authorization: `Bearer ${readToken()}` } }
      );
      if (!res.ok) throw new Error("Download failed.");

      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = doc.originalFilename || doc.name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      addToast(err.message || "Download failed.", "error");
    }
  };

  // ── Delete ──────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this document? This cannot be undone.")) return;
    try {
      const res  = await authFetch(`/api/recruiter/documents/${id}`, { method: "DELETE" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Could not delete document.");
      addToast("Document deleted.");
      setDocuments(prev => prev.filter(d => d.id !== id));
    } catch (err) {
      addToast(err.message || "Could not delete document.", "error");
    }
  };

  // ── Export JSON ─────────────────────────────────────────────────
  const handleExport = () => {
    if (!filteredDocs.length) {
      addToast("No documents to export.", "error");
      return;
    }
    const blob = new Blob(
      [JSON.stringify(filteredDocs, null, 2)],
      { type: "application/json" }
    );
    const url  = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href     = url;
    link.download = selectedEmployee
      ? `${selectedEmployee}-documents.json`
      : "recruiter-documents.json";
    link.click();
    URL.revokeObjectURL(url);
    addToast("Documents exported.");
  };

  // ── Close modal ─────────────────────────────────────────────────
  const closeModal = () => {
    setShowUpload(false);
    setUploadData(EMPTY_FORM);
    setSelectedFile(null);
    setUploadProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ── Render ──────────────────────────────────────────────────────
  return (
    <div className="emphr-app">
      {/* Header */}
      <header className="empheader">
        <h2>HR Document Management</h2>
        <div className="empheader-actions">
          <input
            type="text"
            placeholder="Search documents..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select
            value={selectedEmployee}
            onChange={e => setSelectedEmployee(e.target.value)}
          >
            <option value="">All Employees</option>
            {employees.map(emp => (
              <option key={emp} value={emp}>{emp}</option>
            ))}
          </select>
          <select
            value={typeFilter}
            onChange={e => setTypeFilter(e.target.value)}
          >
            <option value="">All Types</option>
            {DOC_TYPES.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          <button className="empbtn" onClick={() => setShowUpload(true)}>
            ⬆️ Upload Document
          </button>
          <button className="empbtn save-all" onClick={handleExport}>
            💾 Export
          </button>
        </div>
      </header>

      {/* Stats bar */}
      <div style={{
        display: "flex", gap: 16, padding: "10px 20px",
        fontSize: 13, color: "#6b7280", borderBottom: "1px solid #f0f0f0",
      }}>
        <span>📁 <strong>{filteredDocs.length}</strong> document{filteredDocs.length !== 1 ? "s" : ""}</span>
        {selectedEmployee && <span>👤 {selectedEmployee}</span>}
        {typeFilter && <span>🏷️ {DOC_TYPES.find(t => t.value === typeFilter)?.label}</span>}
        <span style={{ marginLeft: "auto" }}>
          🔒 {filteredDocs.filter(d => d.confidential).length} confidential
        </span>
      </div>

      {/* Table */}
      <div className="emptable-container">
        {loading ? (
          <p className="empempty">Loading documents...</p>
        ) : filteredDocs.length === 0 ? (
          <div className="empempty">
            <div style={{ fontSize: 40, marginBottom: 12 }}>📂</div>
            <div>No documents found</div>
            <button
              className="empbtn"
              style={{ marginTop: 12 }}
              onClick={() => setShowUpload(true)}
            >
              Upload your first document
            </button>
          </div>
        ) : (
          <table className="empdoc-table">
            <thead>
              <tr>
                <th>File</th>
                <th>Type</th>
                <th>Employee</th>
                <th>Department</th>
                <th>Size</th>
                <th>Date</th>
                <th>Confidential</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDocs.map(doc => (
                <tr key={doc.id}>
                  <td style={{ maxWidth: 220 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span>{getFileIcon(doc)}</span>
                      <span style={{
                        overflow: "hidden", textOverflow: "ellipsis",
                        whiteSpace: "nowrap", fontWeight: 500,
                      }}>
                        {doc.name}
                      </span>
                    </div>
                    {doc.notes && (
                      <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
                        {doc.notes.slice(0, 60)}{doc.notes.length > 60 ? "…" : ""}
                      </div>
                    )}
                  </td>
                  <td>
                    <span style={{
                      background: "#f3f4f6", borderRadius: 6,
                      padding: "2px 8px", fontSize: 12,
                    }}>
                      {DOC_TYPES.find(t => t.value === doc.type)?.label || doc.type || "—"}
                    </span>
                  </td>
                  <td>{doc.employee || "—"}</td>
                  <td>{doc.department || "—"}</td>
                  <td>{doc.size || "—"}</td>
                  <td>{doc.uploadDate || "—"}</td>
                  <td>
                    {doc.confidential
                      ? <span style={{ color: "#dc2626", fontWeight: 600 }}>🔒 Yes</span>
                      : <span style={{ color: "#6b7280" }}>No</span>
                    }
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: 6 }}>
                      {doc.fileUrl && (
                        <button
                          className="empbtn small"
                          onClick={() => handleDownload(doc)}
                          title="Download file"
                        >
                          ⬇️
                        </button>
                      )}
                      <button
                        className="empbtn small text-light"
                        onClick={() => handleDelete(doc.id)}
                        title="Delete document"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Upload Modal */}
      {showUpload && (
        <div className="empmodal-overlay" onClick={closeModal}>
          <div className="empmodal" onClick={e => e.stopPropagation()}
            style={{ maxWidth: 520, width: "100%" }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
              <h3 style={{ margin: 0 }}>Upload Document</h3>
              <button
                onClick={closeModal}
                style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#6b7280" }}
              >✕</button>
            </div>

            <form onSubmit={handleUpload}>

              {/* Drop zone */}
              <div
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                style={{
                  border: `2px dashed ${dragOver ? "#263012" : selectedFile ? "#16a34a" : "#d1d5db"}`,
                  borderRadius: 10, padding: "22px 16px", textAlign: "center",
                  cursor: "pointer", marginBottom: 16, transition: "all 0.2s",
                  background: dragOver ? "#f2f5ea" : selectedFile ? "#f0fdf4" : "transparent",
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  style={{ display: "none" }}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp,.txt,.csv"
                  onChange={e => handleFileSelect(e.target.files[0])}
                />
                {selectedFile ? (
                  <div>
                    <div style={{ fontSize: 28 }}>
                      {MIME_ICONS[selectedFile.type] || "📁"}
                    </div>
                    <div style={{ fontWeight: 600, marginTop: 6, fontSize: 14 }}>
                      {selectedFile.name}
                    </div>
                    <div style={{ color: "#6b7280", fontSize: 12 }}>
                      {formatSize(selectedFile.size)} · Click to change file
                    </div>
                  </div>
                ) : (
                  <div>
                    <div style={{ fontSize: 32 }}>📂</div>
                    <div style={{ fontWeight: 600, marginTop: 6 }}>
                      Drag & drop or click to select
                    </div>
                    <div style={{ color: "#9ca3af", fontSize: 12, marginTop: 4 }}>
                      PDF, DOC, DOCX, XLS, XLSX, JPG, PNG, TXT, CSV · Max 10MB
                    </div>
                  </div>
                )}
              </div>

              {/* Upload progress bar */}
              {uploadProgress > 0 && uploadProgress < 100 && (
                <div style={{
                  height: 4, background: "#f3f4f6", borderRadius: 4,
                  marginBottom: 14, overflow: "hidden",
                }}>
                  <div style={{
                    height: "100%", background: "#263012",
                    width: `${uploadProgress}%`, transition: "width 0.3s",
                    borderRadius: 4,
                  }}/>
                </div>
              )}

              {/* Document name */}
              <div style={fieldStyle}>
                <label style={labelStyle}>Document name *</label>
                <input
                  type="text" required
                  placeholder="e.g. John Smith Offer Letter"
                  value={uploadData.name}
                  onChange={e => setUploadData(p => ({ ...p, name: e.target.value }))}
                  style={inputStyle}
                />
              </div>

              {/* Type + Employee */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Document type *</label>
                  <select
                    required value={uploadData.type}
                    onChange={e => setUploadData(p => ({ ...p, type: e.target.value }))}
                    style={inputStyle}
                  >
                    <option value="">Select type</option>
                    {DOC_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Employee name</label>
                  <input
                    type="text" placeholder="Optional"
                    value={uploadData.employee}
                    onChange={e => setUploadData(p => ({ ...p, employee: e.target.value }))}
                    style={inputStyle}
                  />
                </div>
              </div>

              {/* Department */}
              <div style={fieldStyle}>
                <label style={labelStyle}>Department</label>
                <input
                  type="text" placeholder="e.g. Engineering, HR"
                  value={uploadData.department}
                  onChange={e => setUploadData(p => ({ ...p, department: e.target.value }))}
                  style={inputStyle}
                />
              </div>

              {/* Notes */}
              <div style={fieldStyle}>
                <label style={labelStyle}>Notes</label>
                <textarea
                  rows={2} placeholder="Any additional context..."
                  value={uploadData.notes}
                  onChange={e => setUploadData(p => ({ ...p, notes: e.target.value }))}
                  style={{ ...inputStyle, resize: "vertical" }}
                />
              </div>

              {/* Confidential */}
              <label style={{
                display: "flex", alignItems: "center", gap: 8,
                marginBottom: 16, cursor: "pointer", fontSize: 14,
              }}>
                <input
                  type="checkbox"
                  checked={uploadData.confidential}
                  onChange={e => setUploadData(p => ({ ...p, confidential: e.target.checked }))}
                  style={{ width: 15, height: 15 }}
                />
                🔒 Mark as confidential
              </label>

              {/* Actions */}
              <div style={{ display: "flex", gap: 10 }}>
                <button
                  type="button"
                  className="empbtn"
                  onClick={closeModal}
                  disabled={saving}
                  style={{ flex: 1, background: "#f3f4f6", color: "#374151" }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="empbtn"
                  disabled={saving || !selectedFile}
                  style={{ flex: 2, opacity: saving || !selectedFile ? 0.6 : 1 }}
                >
                  {saving ? "⏳ Uploading..." : "⬆️ Upload"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Toasts */}
      <div className="emptoast-container">
        {toasts.map(t => (
          <Toast key={t.id} message={t.message} type={t.type}
            onClose={() => removeToast(t.id)} />
        ))}
      </div>
    </div>
  );
};

export default HrDocumentManager;

// ── Inline styles ─────────────────────────────────────────────────
const fieldStyle  = { display: "flex", flexDirection: "column", gap: 4, marginBottom: 12 };
const labelStyle  = { fontSize: 12, fontWeight: 600, color: "#374151" };
const inputStyle  = {
  padding: "8px 11px", borderRadius: 7,
  border: "1.5px solid #d1d5db", fontSize: 14,
  fontFamily: "inherit", outline: "none",
  background: "transparent", color: "inherit",
};
